import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Store, Clock, Utensils, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiClient';
import ImagePickerField from '../components/ImagePickerField';
import TruckLocationPicker from '../components/TruckLocationPicker';
import { hasExactLocation } from '../lib/truckLocation';

const CAMPUS_LOCATIONS = [
  'Sidney Smith Hall',
  'Robarts Library',
  'Bahen Centre',
  'Medical Sciences Building',
  'Hart House',
  'University College',
  'Gerstein Library',
  'Knox College',
  'Innis College',
  'New College'
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, day) => {
  acc[day] = {
    active: ['saturday', 'sunday'].includes(day) ? false : true,
    open: '10:00',
    close: '18:00'
  };
  return acc;
}, {});

export default function VendorRegister() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Truck Info
  const [truckInfo, setTruckInfo] = useState({
    name: '',
    description: '',
    photo_url: '',
    location: '',
    cuisine_type: '',
    payout_email: user?.email || ''
  });
  
  const [exactLocation, setExactLocation] = useState(null);

  // Step 2: Operating Hours
  const [operatingHours, setOperatingHours] = useState(DEFAULT_HOURS);
  const [maxOrdersPerSlot, setMaxOrdersPerSlot] = useState(10);
  
  // Step 3: Menu Items
  const [menuItems, setMenuItems] = useState([{
    name: '',
    description: '',
    price: '',
    photo_url: '',
    prep_time_minutes: '10',
    modifiers: '',
    category: ''
  }]);

  const validateStep1 = () => {
    if (!truckInfo.name.trim()) {
      toast.error('Truck name is required');
      return false;
    }
    if (!truckInfo.description.trim()) {
      toast.error('Description is required');
      return false;
    }
    if (truckInfo.description.length > 200) {
      toast.error('Description must be 200 characters or less');
      return false;
    }
    if (!truckInfo.location) {
      toast.error('Location is required');
      return false;
    }
    if (!EMAIL_PATTERN.test(truckInfo.payout_email.trim())) {
      toast.error('A valid Interac e-Transfer email is required for payouts');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const activeDays = Object.values(operatingHours).filter(h => h.active);
    if (activeDays.length === 0) {
      toast.error('At least one operating day is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    const validItems = menuItems.filter(item => item.name.trim() && item.price);
    if (validItems.length === 0) {
      toast.error('At least one menu item is required');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const addMenuItem = () => {
    setMenuItems(prev => [...prev, {
      name: '',
      description: '',
      price: '',
      photo_url: '',
      prep_time_minutes: '10',
      modifiers: '',
      category: ''
    }]);
  };

  const removeMenuItem = (index) => {
    if (menuItems.length > 1) {
      setMenuItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateMenuItem = (index, field, value) => {
    setMenuItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);

    // Build operating hours in the correct format
    const formattedHours = {};
    Object.entries(operatingHours).forEach(([day, config]) => {
      if (config.active) {
        formattedHours[day] = {
          open: config.open,
          close: config.close
        };
      }
    });

    // Build menu items
    const formattedItems = menuItems
      .filter(item => item.name.trim() && item.price)
      .map(item => ({
        name: item.name.trim(),
        description: item.description.trim(),
        price: parseFloat(item.price),
        photo_url: item.photo_url || null,
        prep_time_minutes: parseInt(item.prep_time_minutes) || 10,
        modifiers: item.modifiers ? item.modifiers.split(',').map(m => ({ name: m.trim() })) : null,
        category: item.category || null
      }));

    const payload = {
      name: truckInfo.name.trim(),
      description: truckInfo.description.trim(),
      photo_url: truckInfo.photo_url || null,
      location: truckInfo.location,
      exact_location: hasExactLocation(exactLocation)
        ? { lat: Number(exactLocation.lat), lng: Number(exactLocation.lng), note: exactLocation.note || null }
        : null,
      cuisine_type: truckInfo.cuisine_type || null,
      payout_email: truckInfo.payout_email.trim(),
      operating_hours: formattedHours,
      max_orders_per_slot: maxOrdersPerSlot,
      menu_items: formattedItems
    };

    try {
      const response = await apiFetch('/api/vendor/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await checkAuth(); // Refresh user data
        let hasApprovedTruck = false;
        try {
          const statusRes = await apiFetch('/api/vendor/status');
          if (statusRes.ok) {
            const status = await statusRes.json();
            hasApprovedTruck = Boolean(status?.is_approved);
          }
        } catch {
          /* fall through to the pending page */
        }
        if (hasApprovedTruck) {
          toast.success('New truck submitted — it will go live once an admin approves it.');
          navigate('/dashboard');
        } else {
          toast.success('Registration submitted!');
          navigate('/vendor/pending');
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const stepIndicator = (num, label) => (
    <div className="flex items-center gap-2">
      <div className={`
        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
        ${step >= num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
      `}>
        {step > num ? <Check className="w-4 h-4" /> : num}
      </div>
      <span className={`text-sm hidden sm:block ${step >= num ? 'text-foreground' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div
        className="bg-card border-b px-6 py-4 sticky z-50"
        style={{ top: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-2xl mx-auto flex items-start justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <h1 className="font-bold">Register Your Food Truck</h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {stepIndicator(1, 'Truck Info')}
          <div className={`flex-1 h-0.5 mx-2 ${step > 1 ? 'bg-primary' : 'bg-muted'}`} />
          {stepIndicator(2, 'Hours & Slots')}
          <div className={`flex-1 h-0.5 mx-2 ${step > 2 ? 'bg-primary' : 'bg-muted'}`} />
          {stepIndicator(3, 'Menu')}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Truck Info */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Store className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Truck Information</h2>
                  <p className="text-sm text-muted-foreground">Tell us about your food truck</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Truck Name *</Label>
                  <Input
                    id="name"
                    value={truckInfo.name}
                    onChange={(e) => setTruckInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Taco Express"
                    data-testid="truck-name"
                  />
                </div>

                <div>
                  <Label htmlFor="description">
                    Description * <span className="text-muted-foreground">({truckInfo.description.length}/200)</span>
                  </Label>
                  <textarea
                    id="description"
                    value={truckInfo.description}
                    onChange={(e) => setTruckInfo(prev => ({ ...prev, description: e.target.value.slice(0, 200) }))}
                    placeholder="What makes your food special?"
                    className="w-full p-3 rounded-lg bg-muted border-0 resize-none text-sm"
                    rows={3}
                    data-testid="truck-description"
                  />
                </div>

                <div>
                  <ImagePickerField
                    label="Cover photo"
                    value={truckInfo.photo_url}
                    onChange={(url) => setTruckInfo((prev) => ({ ...prev, photo_url: url }))}
                    testId="truck-photo"
                  />
                </div>

                <div>
                  <Label htmlFor="location">Campus Location *</Label>
                  <select
                    id="location"
                    value={truckInfo.location}
                    onChange={(e) => setTruckInfo(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full p-3 rounded-lg bg-muted border-0 text-sm"
                    data-testid="truck-location"
                  >
                    <option value="">Select a location</option>
                    {CAMPUS_LOCATIONS.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2" data-testid="exact-location-section">
                  <div>
                    <Label>Exact spot (optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Pin exactly where your truck parks so students can walk straight to you. You can change it later from your dashboard.
                    </p>
                  </div>
                  <TruckLocationPicker
                    value={exactLocation}
                    onChange={setExactLocation}
                  />
                </div>

                <div>
                  <Label htmlFor="cuisine_type">Cuisine Type</Label>
                  <Input
                    id="cuisine_type"
                    value={truckInfo.cuisine_type}
                    onChange={(e) => setTruckInfo(prev => ({ ...prev, cuisine_type: e.target.value }))}
                    placeholder="e.g., Mexican, Vietnamese, American"
                    data-testid="truck-cuisine"
                  />
                </div>

                <div>
                  <Label htmlFor="payout_email">Interac e-Transfer email *</Label>
                  <Input
                    id="payout_email"
                    type="email"
                    autoComplete="email"
                    value={truckInfo.payout_email}
                    onChange={(e) => setTruckInfo(prev => ({ ...prev, payout_email: e.target.value }))}
                    placeholder="payouts@yourtruck.com"
                    data-testid="truck-payout-email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your daily earnings for this truck are sent here by Interac e-Transfer.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Operating Hours */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Operating Hours & Slots</h2>
                  <p className="text-sm text-muted-foreground">Set your schedule and capacity</p>
                </div>
              </div>

              <div className="space-y-3">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-4 p-3 bg-card rounded-lg border">
                    <Switch
                      checked={operatingHours[day].active}
                      onCheckedChange={(checked) => setOperatingHours(prev => ({
                        ...prev,
                        [day]: { ...prev[day], active: checked }
                      }))}
                      data-testid={`day-toggle-${day}`}
                    />
                    <span className="w-24 capitalize font-medium">{day}</span>
                    {operatingHours[day].active ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={operatingHours[day].open}
                          onChange={(e) => setOperatingHours(prev => ({
                            ...prev,
                            [day]: { ...prev[day], open: e.target.value }
                          }))}
                          className="w-auto"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={operatingHours[day].close}
                          onChange={(e) => setOperatingHours(prev => ({
                            ...prev,
                            [day]: { ...prev[day], close: e.target.value }
                          }))}
                          className="w-auto"
                        />
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Closed</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-card rounded-lg border p-4">
                <Label htmlFor="maxOrders">Max Orders Per Pickup Slot</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  This caps how many students can book the same pickup window
                </p>
                <select
                  id="maxOrders"
                  value={maxOrdersPerSlot}
                  onChange={(e) => setMaxOrdersPerSlot(parseInt(e.target.value))}
                  className="w-full p-3 rounded-lg bg-muted border-0 text-sm"
                  data-testid="max-orders"
                >
                  {[3, 5, 10, 15, 20].map(n => (
                    <option key={n} value={n}>{n} orders per slot</option>
                  ))}
                </select>
              </div>
            </motion.div>
          )}

          {/* Step 3: Menu */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Menu Setup</h2>
                  <p className="text-sm text-muted-foreground">Add at least one item to your menu</p>
                </div>
              </div>

              <div className="space-y-4">
                {menuItems.map((item, index) => (
                  <div key={index} className="bg-card rounded-lg border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Item {index + 1}</span>
                      {menuItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMenuItem(index)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={item.name}
                          onChange={(e) => updateMenuItem(index, 'name', e.target.value)}
                          placeholder="Item name"
                          data-testid={`menu-item-name-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Menu price ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.price}
                          onChange={(e) => updateMenuItem(index, 'price', e.target.value)}
                          placeholder="9.99"
                          data-testid={`menu-item-price-${index}`}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Students see this exact price before checkout taxes and fees.
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateMenuItem(index, 'description', e.target.value)}
                        placeholder="Brief description"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Category</Label>
                        <Input
                          value={item.category}
                          onChange={(e) => updateMenuItem(index, 'category', e.target.value)}
                          placeholder="e.g., Mains, Sides"
                        />
                      </div>
                      <div>
                        <Label>Prep Time (min)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.prep_time_minutes}
                          onChange={(e) => updateMenuItem(index, 'prep_time_minutes', e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Modifiers (comma-separated)</Label>
                      <Input
                        value={item.modifiers}
                        onChange={(e) => updateMenuItem(index, 'modifiers', e.target.value)}
                        placeholder="e.g., extra spicy, no onions, add cheese"
                      />
                    </div>

                    <div>
                      <ImagePickerField
                        label="Photo"
                        value={item.photo_url}
                        onChange={(url) => updateMenuItem(index, 'photo_url', url)}
                        testId={`menu-item-photo-${index}`}
                      />
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  onClick={addMenuItem}
                  className="w-full"
                  data-testid="add-menu-item"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Item
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={prevStep} data-testid="prev-step">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < 3 ? (
            <Button onClick={nextStep} data-testid="next-step">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} data-testid="submit-registration">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Registration
                  <Check className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
